import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthMethods, User } from '@studio/shared';
import { api } from './api';

interface AuthState {
  user: User | null;
  loading: boolean;
  /** true while no account exists yet — the first person to sign in becomes admin */
  bootstrap: boolean;
  /** which sign-in methods this server offers */
  methods: AuthMethods;
  /** false when the server could not be reached at all, so the sign-in screen can say so */
  reachable: boolean;
  /** leave the SPA for the CMS handshake, coming back to `returnTo` */
  signInWithSso: (returnTo?: string) => void;
  /** development only, and refused by the server unless debug login is on */
  debugLogin: (name: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Assume neither method until `/me` answers, so nothing is offered prematurely. */
const NO_METHODS: AuthMethods = { sso: false, debug: false };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [bootstrap, setBootstrap] = useState(false);
  const [methods, setMethods] = useState<AuthMethods>(NO_METHODS);
  const [loading, setLoading] = useState(true);
  const [reachable, setReachable] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.me();
      setUser(res.user);
      setBootstrap(res.bootstrap);
      setMethods(res.auth ?? NO_METHODS);
      setReachable(true);
    } catch {
      /*
       * The server could not be reached or errored. Leaving `methods` at NO_METHODS made
       * the sign-in screen claim "single sign-on is not configured on this server", which
       * sends the reader looking for a configuration problem that is not there.
       */
      setReachable(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * Any API call answering 401 means this session is over. The app renders the sign-in
   * screen from `user === null`, so clearing it is all that is needed — and it is much
   * better than what happened before, which was every page spinning indefinitely because
   * its one fetch had rejected and nothing was listening.
   */
  useEffect(() => {
    const onUnauthenticated = () => {
      setUser((current) => (current === null ? current : null));
      setLoading(false);
    };
    window.addEventListener('studio:unauthenticated', onUnauthenticated);
    return () => window.removeEventListener('studio:unauthenticated', onUnauthenticated);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      bootstrap,
      methods,
      reachable,
      signInWithSso: (returnTo) => {
        const here = `${window.location.pathname}${window.location.search}`;
        // coming back to /login would just show the sign-in page again
        const target = returnTo ?? (window.location.pathname === '/login' ? '/' : here);
        // a full page load, not a fetch: the CMS answers with a redirect chain
        // that only the browser can follow
        window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(target)}`;
      },
      debugLogin: async (name) => {
        const res = await api.debugLogin(name);
        setUser(res.user);
        setBootstrap(false);
      },
      logout: async () => {
        await api.logout();
        setUser(null);
      },
    }),
    [user, loading, bootstrap, methods, reachable],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
